import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, Handshake, ClipboardList, TrendingUp, Users, CheckCircle2, MessageSquare } from "lucide-react";
import { METRICS, COMMUNITY_PARTNERS } from "@/lib/postEvent";

const IMG = "/highlights";
const FOLLOWUP = ["No update yet", "Reconnected since the Summit", "Follow-up meeting scheduled", "In active discussion", "Needs an introduction", "On hold"];
const STAGE = ["No update yet", "Introduction", "Follow-Up", "Opportunity Identified", "Proposal", "Contract", "Second-Tier Spend"];

function ShareProgress() {
  const [form, setForm] = useState({ organization: "", follow_up_status: "No update yet", opportunity_stage: "No update yet", introduction_support: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization.trim()) { toast.error("Please enter your organization."); return; }
    setBusy(true);
    try { await api.post("/engagement/progress", form); setDone(true); toast.success("Thank you — your update was shared privately with the program team."); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  const field = "w-full h-12 px-4 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-[#A39171]";
  if (done) return (
    <div className="border border-[#A39171]/40 bg-[#FBF9F5] p-8 flex items-start gap-4" data-testid="progress-thanks">
      <CheckCircle2 size={22} className="text-[#A39171] shrink-0 mt-0.5" />
      <div><h3 className="serif text-xl">Thank you.</h3><p className="mt-2 text-sm text-zinc-600">Your progress update has been shared privately with the program team.</p></div>
    </div>
  );
  return (
    <form onSubmit={submit} className="border border-zinc-200 p-6 md:p-8 space-y-5" data-testid="progress-form">
      <div>
        <label className="overline">Organization</label>
        <input className={`${field} mt-2`} value={form.organization} onChange={set("organization")} placeholder="Your company or organization" data-testid="progress-org" />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="overline">Follow-Up Status</label>
          <select className={`${field} mt-2`} value={form.follow_up_status} onChange={set("follow_up_status")} data-testid="progress-followup">
            {FOLLOWUP.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="overline">Opportunity Stage</label>
          <select className={`${field} mt-2`} value={form.opportunity_stage} onChange={set("opportunity_stage")} data-testid="progress-stage">
            {STAGE.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="overline">Introduction or Support Needed <span className="text-zinc-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className={`${field.replace("h-12", "min-h-[96px] py-3")} mt-2`} value={form.introduction_support} onChange={set("introduction_support")} placeholder="Is there an introduction or support we can help with?" data-testid="progress-support" />
      </div>
      <button className="btn-black" disabled={busy} data-testid="progress-submit">{busy ? "Sharing…" : "Share Your Progress"} <ArrowRight size={15} /></button>
      <p className="text-xs text-zinc-400">Individual submissions are kept private and visible only to authorized program administrators.</p>
    </form>
  );
}
const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } };

const JOURNEY = ["Introduction", "Follow-Up", "Opportunity", "Proposal", "Contract", "Second-Tier Spend"];
const FEEDBACK = [
  { v: "4.77", suf: "/ 5", l: "Average Overall Experience" },
  { v: "4.50", suf: "/ 5", l: "Average Connection Value" },
  { v: "4.15", suf: "/ 5", l: "Average Business Matchmaking Score" },
  { v: "95.5", suf: "%", l: "Identified a definite or potential opportunity / follow-up" },
];
const PILLARS = [
  { icon: Handshake, t: "Connect", d: "Continue conversations and internal introductions." },
  { icon: ClipboardList, t: "Track", d: "Record opportunities, follow-ups, proposals, contracts, and supplier relationships." },
  { icon: TrendingUp, t: "Measure", d: "Monitor second-tier SBE spend and progress toward Travel + Leisure's 25% growth objective." },
];

function Stat({ value, label, sub }) {
  return (
    <div data-testid={`impact-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="serif text-5xl md:text-6xl leading-none text-white">{value}</div>
      <div className="mt-3 text-[11px] uppercase tracking-[0.28em] text-[#C9B792]">{label}</div>
      {sub && <div className="mt-1 text-xs text-white/45">{sub}</div>}
    </div>
  );
}

function StoryBlock({ eyebrow, title, body, img, reverse, children }) {
  return (
    <motion.div {...fade} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? "md:[direction:rtl]" : ""}`}>
      <div className="overflow-hidden md:[direction:ltr] bg-zinc-100">
        <img src={img} alt={title} loading="lazy" className="w-full h-[300px] md:h-[440px] object-contain" />
      </div>
      <div className="md:[direction:ltr]">
        <div className="overline text-[#A39171]">{eyebrow}</div>
        <h3 className="serif text-3xl md:text-4xl mt-4 leading-tight">{title}</h3>
        <p className="mt-5 text-zinc-600 leading-relaxed">{body}</p>
        {children}
      </div>
    </motion.div>
  );
}

export default function PostEventHome() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const hero = params.get("hero") === "session" ? "session" : "showcase";
  const heroImg = hero === "session" ? `${IMG}/session-audience.webp` : `${IMG}/matchmaking-2.webp`;

  return (
    <Layout>
      {/* 1 — HERO */}
      <section className="relative bg-[#0A0A0A] text-white overflow-hidden" data-testid="postevent-hero">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Travel + Leisure Strategic Supplier Summit" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        </div>
        <div className="relative container-summit py-24 md:py-36">
          <motion.div {...fade}>
            <div className="text-[11px] uppercase tracking-[0.34em] text-[#C9B792]">Travel + Leisure · September 11, 2026 · Orlando, FL</div>
            <h1 className="serif text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.04]">
              Travel + Leisure<br />Strategic Supplier Summit <span className="text-[#A39171]">2026</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
              125 attendees. 13 Prime Suppliers. 67 formal meetings.<br className="hidden sm:block" />
              <span className="text-white">One connected supplier ecosystem.</span>
            </p>
            <p className="mt-5 text-sm md:text-base text-white/55 max-w-2xl leading-relaxed">
              The inaugural Summit brought together Prime Suppliers, Small Business Enterprises, Travel + Leisure
              executives and sourcing professionals, community leaders, and business partners for a full day of
              education, networking, supplier engagement, and Business Matchmaking.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/impact" className="btn-gold" data-testid="hero-cta-impact">View Event Impact <ArrowRight size={15} /></Link>
              <Link to="/highlights" className="btn-outline-white" data-testid="hero-cta-highlights">Explore Event Highlights</Link>
              <Link to="/survey" className="btn-outline-white" data-testid="hero-cta-survey">Complete Post-Event Survey</Link>
              {user && <Link to="/dashboard" className="btn-outline-white" data-testid="hero-cta-dashboard">View My Dashboard</Link>}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 — IMPACT METRICS */}
      <section className="bg-[#111111] text-white border-t border-white/10" data-testid="postevent-impact-strip">
        <div className="container-summit py-16 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-y-12 gap-x-8">
            <Stat value={METRICS.attendees} label="Attendees" />
            <Stat value={METRICS.primes} label="Prime Suppliers" />
            <Stat value={METRICS.formal} label="Formal Meetings" />
            <Stat value={METRICS.sbePrime} label="SBE–Prime Matchmaking" />
            <Stat value={METRICS.tlPrime} label="T+L–Prime Sourcing" />
          </div>
        </div>
      </section>

      {/* 3 — STRATEGIC OBJECTIVE */}
      <section className="bg-white" data-testid="postevent-objective">
        <div className="container-summit py-20 md:py-28">
          <motion.div {...fade} className="max-w-3xl">
            <div className="overline text-[#A39171]">The Strategic Objective</div>
            <h2 className="serif text-3xl md:text-5xl mt-4 leading-tight">From introductions to measurable second-tier growth.</h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">
              The Summit was designed to do more than create meaningful conversations in a single day.
              Travel + Leisure's broader objective is to strengthen its supplier ecosystem and support a
              <span className="text-black font-medium"> 25% increase in second-tier spend</span> with Small Business
              Enterprises through participating Prime Suppliers.
            </p>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              The inaugural Summit established the relationships, sourcing conversations, and business connections
              needed to begin measuring progress toward that goal.
            </p>
          </motion.div>
          <motion.div {...fade} className="mt-12 flex flex-wrap items-center gap-x-2 gap-y-3" data-testid="objective-journey">
            {JOURNEY.map((step, i) => (
              <React.Fragment key={step}>
                <span className={`inline-flex items-center px-4 py-2 text-xs uppercase tracking-[0.16em] border ${i === JOURNEY.length - 1 ? "border-[#A39171] bg-[#A39171]/10 text-[#8a774f] font-medium" : "border-zinc-300 text-zinc-600"}`}>{step}</span>
                {i < JOURNEY.length - 1 && <ArrowRight size={14} className="text-[#A39171]" />}
              </React.Fragment>
            ))}
          </motion.div>
          <p className="mt-5 text-xs text-zinc-400">The 25% second-tier spend increase is a Travel + Leisure strategic goal, not a completed result.</p>
        </div>
      </section>

      {/* 4 — SURVEY OUTCOMES */}
      <section className="bg-[#0A0A0A] text-white" data-testid="postevent-feedback">
        <div className="container-summit py-20 md:py-24">
          <motion.div {...fade}>
            <div className="overline text-[#C9B792]">Participant Feedback</div>
            <h2 className="serif text-3xl md:text-5xl mt-4 leading-tight">Strong signals of business opportunity.</h2>
          </motion.div>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {FEEDBACK.map((f) => (
              <div key={f.l} data-testid={`feedback-${f.l.toLowerCase().replace(/[^a-z]+/g,"-").slice(0,20)}`}>
                <div className="serif text-5xl md:text-6xl leading-none">{f.v}<span className="text-2xl md:text-3xl text-[#A39171] ml-1">{f.suf}</span></div>
                <div className="mt-3 text-xs text-white/60 leading-snug max-w-[220px]">{f.l}</div>
              </div>
            ))}
          </div>
          <p className="mt-12 text-sm text-white/60 max-w-3xl leading-relaxed">
            Post-event feedback reinforced the value of direct access to Travel + Leisure leadership, Prime Suppliers,
            sourcing professionals, and qualified SBEs. Of the current survey respondents, 21 of 22 identified either a
            definite future business opportunity or a potential follow-up resulting from the Summit.
          </p>
          <p className="mt-4 text-xs text-white/40">Survey results as of September 18, 2026 · 22 respondents. Responses may continue to update during the post-event response period.</p>
        </div>
      </section>

      {/* 5 — THE EXPERIENCE */}
      <section className="container-summit py-20 md:py-28" data-testid="postevent-experience">
        <motion.div {...fade} className="max-w-3xl">
          <div className="overline text-[#A39171]">The Experience</div>
          <h2 className="serif text-3xl md:text-5xl mt-4 leading-tight">A day built to strengthen the supplier ecosystem.</h2>
          <p className="mt-6 text-zinc-600 leading-relaxed">
            The Travel + Leisure Strategic Supplier Summit was designed to strengthen supplier relationships, expand
            second-tier opportunities, and create measurable pathways for SBE growth across the Travel + Leisure
            supplier ecosystem. The program combined executive engagement, educational sessions, a curated Supplier
            Showcase, and structured Business Matchmaking to connect qualified SBEs with Prime Suppliers and the
            Travel + Leisure sourcing team.
          </p>
        </motion.div>
        <div className="mt-16 space-y-20 md:space-y-28">
          <StoryBlock eyebrow="Executive & Educational Sessions" title="Leadership set the vision for supplier growth"
            body="Travel + Leisure leadership opened the day by sharing the company's vision for supplier growth, followed by educational sessions and executive dialogue focused on partnership, second-tier opportunity, and long-term supplier engagement."
            img={`${IMG}/session-audience.webp`} />
          <StoryBlock reverse eyebrow="Supplier Showcase" title="A curated Supplier Showcase floor"
            body="Prime Suppliers presented their capabilities through branded Showcase stations while Travel + Leisure leaders, SBEs, and community partners circulated throughout the three-hour open floor. The Showcase created an informal environment for conversations and introductions beyond the formal matchmaking schedule."
            img={`${IMG}/showcase-people-1.webp`} />
          <StoryBlock eyebrow="Business Matchmaking" title="67 formal meetings, precisely scheduled"
            body="Structured Business Matchmaking connected qualified SBEs with Prime Suppliers and the Travel + Leisure sourcing team across dedicated tables — designed to create meaningful business connections."
            img={`${IMG}/matchmaking-2.webp`}>
            <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
              <div><div className="serif text-3xl">49</div><div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">SBE–Prime Meetings</div></div>
              <div><div className="serif text-3xl">18</div><div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">T+L–Prime Sourcing</div></div>
            </div>
            <p className="mt-6 text-sm text-zinc-600">Every participating Prime Supplier had at least one scheduled meeting with a member of the Travel + Leisure sourcing team.</p>
          </StoryBlock>
          <StoryBlock reverse eyebrow="Networking & Relationships" title="Relationships that extend beyond the agenda"
            body="Between formal sessions and scheduled meetings, attendees connected throughout the Supplier Showcase — creating introductions and relationships that extended beyond the structured agenda. These organic conversations became an important part of the Summit experience and reinforced the value of bringing the full supplier ecosystem together in one environment."
            img={`${IMG}/matchmaking-1.webp`} />
        </div>
      </section>

      {/* 6 — COMMUNITY */}
      <section className="bg-zinc-50 border-y border-zinc-200" data-testid="postevent-community">
        <div className="container-summit py-20 md:py-24">
          <motion.div {...fade}>
            <div className="overline text-[#A39171]">Community Participation</div>
            <h2 className="serif text-3xl md:text-4xl mt-4">Community leaders & partners</h2>
            <p className="mt-4 text-zinc-600 max-w-2xl">Regional leaders and community organizations joined the Summit to support supplier development and second-tier opportunity across Central Florida.</p>
          </motion.div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {COMMUNITY_PARTNERS.map((p) => (
              <div key={p.name} className="bg-white px-6 py-8 flex items-center justify-center min-h-[140px]" title={p.name}>
                <img src={p.logo} alt={p.name} loading="lazy" className="max-h-20 max-w-[170px] object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — CONTINUE THE CONVERSATION */}
      <section className="container-summit py-20 md:py-28" data-testid="postevent-continue">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <motion.div {...fade}>
            <div className="overline text-[#A39171]">Continue the Conversation</div>
            <h2 className="serif text-3xl md:text-5xl mt-4 leading-tight">Reconnect, introduce, and explore what's next.</h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">
              The Summit opened the door — now keep it moving. Reconnect with the people you met, share ideas,
              make introductions, and explore new ways to work together across the Travel + Leisure supplier ecosystem.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/directory" className="btn-black" data-testid="continue-directory"><Users size={15} /> View Supplier Directory</Link>
              <a href="#share-progress" className="btn-outline-black" data-testid="continue-share"><MessageSquare size={15} /> Share Your Progress</a>
            </div>
          </motion.div>
          <motion.div {...fade} id="share-progress">
            <ShareProgress />
          </motion.div>
        </div>
      </section>

      {/* 8 — WHAT HAPPENS NEXT */}
      <section className="container-summit py-20 md:py-28" data-testid="postevent-next">
        <motion.div {...fade} className="max-w-3xl">
          <div className="overline text-[#A39171]">What Happens Next</div>
          <h2 className="serif text-3xl md:text-5xl mt-4 leading-tight">The Summit was the starting point.</h2>
          <p className="mt-6 text-zinc-600 leading-relaxed">
            The next phase is to track the relationships, opportunities, and business outcomes created through the
            Summit. Participating Prime Suppliers, SBEs, and authorized Travel + Leisure users will retain access to
            the platform so progress can be measured throughout the year and compared from one Summit to the next.
          </p>
        </motion.div>
        <div className="mt-12 grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {PILLARS.map((p) => (
            <div key={p.t} className="bg-white p-8">
              <p.icon size={22} className="text-[#A39171]" strokeWidth={1.5} />
              <h3 className="serif text-2xl mt-5">{p.t}</h3>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
        <Link to="/dashboard" className="btn-black mt-10 inline-flex" data-testid="next-cta-signin">
          Sign in to the Supplier Engagement Platform <ArrowRight size={15} />
        </Link>
      </section>

      {/* 8 — POST-EVENT SURVEY (open) */}
      <section className="bg-[#0A0A0A] text-white" data-testid="postevent-survey-band">
        <div className="container-summit py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="overline text-[#C9B792]">Post-Event Survey</div>
            <h2 className="serif text-3xl md:text-4xl mt-3">Tell us about your Summit experience.</h2>
            <p className="mt-3 text-white/60 max-w-xl">Your feedback helps us measure impact and shape future supplier engagement. It takes less than two minutes.</p>
          </div>
          <Link to="/survey" className="btn-gold shrink-0" data-testid="postevent-survey-cta">Share Your Feedback <ArrowRight size={15} /></Link>
        </div>
      </section>

      {/* 9 — POWERED BY DISPLAY AMERICA */}
      <section className="container-summit py-20 md:py-24" data-testid="postevent-da">
        <motion.div {...fade} className="border border-zinc-200 p-8 md:p-14">
          <div className="overline text-[#A39171]">Powered by Display America</div>
          <h2 className="serif text-3xl md:text-4xl mt-4 max-w-3xl leading-tight">Strategy, production &amp; supplier-engagement platform partner.</h2>
          <p className="mt-5 text-zinc-600 max-w-3xl leading-relaxed">
            Display America designed and managed the event experience and digital operations supporting the
            Travel + Leisure Strategic Supplier Summit.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-3 max-w-4xl">
            {["Event strategy and planning", "Supplier registration and onboarding", "Event Operations Platform",
              "Supplier Showcase production", "Business Matchmaking", "Participant communications",
              "AI Summit Concierge", "Attendee management", "Event logistics",
              "Onsite execution", "Post-event analytics"].map((c) => (
              <div key={c} className="flex items-center gap-3 text-sm text-zinc-700 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#A39171]" /> {c}
              </div>
            ))}
          </div>
          <a href="https://displayamerica.com" target="_blank" rel="noopener noreferrer"
             className="btn-black mt-10 inline-flex" data-testid="postevent-da-cta">
            Explore Display America Event Solutions <ArrowUpRight size={15} />
          </a>
        </motion.div>
      </section>
    </Layout>
  );
}
